import React, { useEffect, useRef } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import Lottie, { type AnimationItem } from 'lottie-web';

import shapeMorphAnimation from './assets/shape-morph-animation.json';

// Module-level state — persists the current frame across remounts
// so the morphing animation continues smoothly without restarting.
let savedFrame = 0;
let hasPlayedBefore = false;

const CONTAINER_STYLE: React.CSSProperties = {
	width: 24,
	height: 24,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	overflow: 'visible',
};

const LOTTIE_WRAPPER_STYLE: React.CSSProperties = {
	width: 16,
	height: 16,
};

type MorphingShapeLoaderProps = {
	testId?: string;
};

export const MorphingShapeLoader = ({ testId }: MorphingShapeLoaderProps): JSX.Element => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const currentRef = containerRef.current;
		if (!currentRef) {
			return;
		}

		const baseAnimationConfig = {
			container: currentRef,
			animationData: shapeMorphAnimation,
			renderer: 'svg' as const,
			loop: true,
		};

		let animationInstance: AnimationItem;

		if (hasPlayedBefore) {
			// Remount — resume from saved frame
			animationInstance = Lottie.loadAnimation({ ...baseAnimationConfig, autoplay: false });
			animationInstance.goToAndPlay(savedFrame, true);
		} else {
			// First mount — start from scratch
			animationInstance = Lottie.loadAnimation({ ...baseAnimationConfig, autoplay: true });
		}

		hasPlayedBefore = true;

		return () => {
			// Save current frame before destroying
			savedFrame = animationInstance.currentFrame;
			animationInstance.destroy();
		};
	}, []);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie container
		<div style={CONTAINER_STYLE} data-testid={testId}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie wrapper */}
			<div style={LOTTIE_WRAPPER_STYLE} ref={containerRef} />
		</div>
	);
};
